import { useCallback, useEffect, useState } from 'react';
import type { CreateInvitationInput } from '@shared/schemas/invitation.schema.js';
import type { InvitationSummary } from '@shared/types.js';
import {
  ApiRequestError,
  createInvitation,
  listInvitations,
  resendInvitation,
  revokeInvitation
} from '@/services/invitation-api';

/** Admin-facing copy for the failures the invite form can provoke. */
const FRIENDLY_MESSAGES: Record<string, string> = {
  already_member: 'That person is already a member of this island.',
  rate_limited: 'You have sent a lot of invitations in the last hour. Try again later.',
  superadmin_self_access: 'Superadmins cannot invite themselves or other superadmins here.',
  forbidden: 'Only island admins can manage invitations.',
  guest_expired: 'Your guest access to this island has expired.',
  // The row exists; only delivery failed, so Resend is the fix.
  email_failed: 'The invitation was created but the email could not be sent. Try Resend.'
};

export function describeInvitationError(error: unknown, fallback: string): string {
  if (error instanceof ApiRequestError && error.code && FRIENDLY_MESSAGES[error.code]) {
    return FRIENDLY_MESSAGES[error.code];
  }
  return error instanceof Error ? error.message : fallback;
}

export interface UseInvitationsResult {
  invitations: InvitationSummary[];
  isLoading: boolean;
  error: string | null;
  /** Resolves to the error message on failure, so the form can show it inline. */
  invite: (input: CreateInvitationInput) => Promise<InvitationSummary>;
  revoke: (invitationId: string) => Promise<void>;
  resend: (invitationId: string) => Promise<void>;
  refresh: () => Promise<void>;
}

/**
 * The pending invitations of one island, plus the four actions an admin takes
 * on them. Every action refreshes the list, so `PendingInvitations` never has to
 * reconcile its own state with the server's.
 */
export function useInvitations(roomId: string | null | undefined): UseInvitationsResult {
  const [invitations, setInvitations] = useState<InvitationSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!roomId) {
      setInvitations([]);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      setInvitations(await listInvitations(roomId));
    } catch (err) {
      setError(describeInvitationError(err, 'Failed to load invitations'));
    } finally {
      setIsLoading(false);
    }
  }, [roomId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const invite = useCallback(
    async (input: CreateInvitationInput) => {
      if (!roomId) {
        throw new Error('No island selected');
      }
      try {
        return await createInvitation(roomId, input);
      } finally {
        // Even on 502 `email_failed` the invitation exists — only delivery
        // failed — so the list must show it, with its Resend button, rather
        // than leave the admin thinking nothing happened.
        await refresh();
      }
    },
    [roomId, refresh]
  );

  const revoke = useCallback(
    async (invitationId: string) => {
      if (!roomId) return;
      await revokeInvitation(roomId, invitationId);
      await refresh();
    },
    [roomId, refresh]
  );

  const resend = useCallback(
    async (invitationId: string) => {
      if (!roomId) return;
      await resendInvitation(roomId, invitationId);
      await refresh();
    },
    [roomId, refresh]
  );

  return { invitations, isLoading, error, invite, revoke, resend, refresh };
}
